import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Button, Form, Modal, Spinner, Pagination, Row, Col, InputGroup, Dropdown } from 'react-bootstrap';
import { 
  FaSearch, FaTimesCircle, FaPlus, FaTrash, FaEdit, 
  FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaFileInvoice, FaBuilding
} from 'react-icons/fa';
import { get, post, put, del } from '../utils/api';
import { useToast } from './ToastContext';
import './SupplierManagement.css';
import ProgressDialog from './ProgressDialog';
import _ from 'lodash';
import NoDataCard from './NoDataCard';

const SupplierManagement = () => {
  const { showToast } = useToast();
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name-asc');

  const [form, setForm] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_number: '',
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  const fetchSuppliers = useCallback(async (query = '', showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    } else {
      setIsSearching(true);
    }
    setError(null);
    try {
      const response = await get(`/suppliers?limit=10000&q=${query}`);
      if (response.success) {
        setSupplierList(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch suppliers.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
      setIsSearching(false);
    }
  }, []);

  const debouncedFetch = useMemo(() => {
    return _.debounce((query) => {
      fetchSuppliers(query, false);
    }, 500);
  }, [fetchSuppliers]);

  useEffect(() => {
    if (!searchQuery) return;
    debouncedFetch(searchQuery);
    return () => {
      if (debouncedFetch.cancel) debouncedFetch.cancel();
    };
  }, [searchQuery, debouncedFetch]);

  useEffect(() => {
    if (searchQuery === '') {
      fetchSuppliers();
    }
  }, [searchQuery, fetchSuppliers]);

  const handleShowModal = (supplier = null) => {
    setCurrentSupplier(supplier);
    if (supplier) {
      setForm({
        name: supplier.name || '',
        contact_person: supplier.contact_person || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        gst_number: supplier.gst_number || '',
      });
    } else {
      setForm({
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        gst_number: '',
      });
    }
    setShowModal(true);
  };

  // Calculate statistics metrics
  const stats = useMemo(() => {
    let total = supplierList.length;
    let gstCount = 0;
    let contactCount = 0;
    let emailCount = 0;
    
    supplierList.forEach(sup => {
      if (sup.gst_number && sup.gst_number.trim()) gstCount++;
      if (sup.contact_person && sup.contact_person.trim()) contactCount++;
      if (sup.email && sup.email.trim()) emailCount++;
    });
    
    return { total, gstCount, contactCount, emailCount };
  }, [supplierList]);

  // Client-side sorting
  const sortedSuppliers = useMemo(() => {
    let list = [...supplierList];
    list.sort((a, b) => {
      if (sortBy === 'name-asc') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'name-desc') {
        return (b.name || '').localeCompare(a.name || '');
      }
      return 0;
    });
    return list;
  }, [supplierList, sortBy]);

  // Client-side pagination
  const itemsPerPage = 8;
  const totalPages = Math.ceil(sortedSuppliers.length / itemsPerPage);
  
  const paginatedSuppliers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedSuppliers.slice(start, start + itemsPerPage);
  }, [sortedSuppliers, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let result;
      if (currentSupplier) {
        result = await put(`/suppliers/${currentSupplier.supplier_id}`, form);
      } else {
        result = await post('/suppliers', form);
      }

      if (result.success) {
        showToast('Operation successful!', 'success');
        handleCloseModal();
        fetchSuppliers();
      } else {
        setError(result.message || 'Operation failed.');
      }
    } catch (error) {
      setError(error.message || 'Operation failed.');
    }
    setLoading(false);
  };

  const handleDelete = (supplierId) => {
    setSupplierToDelete(supplierId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (supplierToDelete) {
      setLoading(true);
      setError(null);
      try {
        const result = await del(`/suppliers/${supplierToDelete}`);
        if (result.success) {
          showToast('Supplier deleted successfully!', 'success');
          fetchSuppliers();
        } else {
          setError(result.message || 'Deletion failed.');
        }
      } catch (error) {
        setError(error.message || 'Deletion failed.');
      }
      setLoading(false);
      setShowConfirmModal(false);
      setSupplierToDelete(null);
    }
  };

  if (error) return <Container fluid className="supplier-container px-0" style={{ color: 'red' }}>Error: {error}</Container>;

  return (
    <Container fluid className="supplier-container px-4 px-sm-5">
      <ProgressDialog show={loading && !showModal} message="Loading suppliers..." />
      
      {/* Header Title Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-white mb-1">Supplier Management</h2>
          <p className="text-muted">Manage store drug vendors, contact points, and invoice billing parameters.</p>
        </div>
        <Button variant="primary" onClick={() => handleShowModal()} className="add-supplier-btn card-hover py-2 px-3 d-flex align-items-center gap-2">
          <FaPlus /> Add Supplier
        </Button>
      </div>

      {/* Stats Row */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <div className="stat-card total-card">
            <div className="stat-icon-wrapper"><FaBuilding /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Vendors</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card gst-card">
            <div className="stat-icon-wrapper"><FaFileInvoice /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.gstCount}</span>
              <span className="stat-label">GST Registered</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card warning-card">
            <div className="stat-icon-wrapper"><FaUser /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.contactCount}</span>
              <span className="stat-label">Contacts Listed</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card stock-card">
            <div className="stat-icon-wrapper"><FaEnvelope /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.emailCount}</span>
              <span className="stat-label">Active Emails</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Unified Filters Control Bar */}
      <div className="control-bar-wrapper mb-3 py-2 px-3 glassmorphic-panel">
        <Row className="g-2 align-items-center">
          <Col xs={12} md={8}>
            <InputGroup className="search-input-group compact">
              <InputGroup.Text className="bg-transparent border-end-0 py-1 px-2">
                <FaSearch className="text-muted" style={{ fontSize: '0.85rem' }} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by vendor name or contact person..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="border-start-0 ps-0 py-1 search-input-field text-sm"
                style={{ fontSize: '0.85rem', height: '36px' }}
              />
              {searchQuery && (
                <Button variant="link" onClick={() => { setSearchQuery(''); setCurrentPage(1); }} className="clear-search-btn text-muted border-start-0 py-0 px-2">
                  <FaTimesCircle style={{ fontSize: '0.85rem' }} />
                </Button>
              )}
            </InputGroup>
          </Col>
          <Col xs={12} md={4}>
            <Dropdown className="w-100">
              <Dropdown.Toggle 
                variant="outline-secondary" 
                className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm"
                style={{ fontSize: '0.85rem', height: '36px', background: 'rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', color: 'var(--text-color)', textAlign: 'left' }}
              >
                {sortBy === 'name-desc' ? 'Sort: Name (Z - A)' : 'Sort: Name (A - Z)'}
              </Dropdown.Toggle>
              <Dropdown.Menu className="filter-dropdown-menu w-100">
                <Dropdown.Item onClick={() => { setSortBy('name-asc'); setCurrentPage(1); }}>Sort: Name (A - Z)</Dropdown.Item>
                <Dropdown.Item onClick={() => { setSortBy('name-desc'); setCurrentPage(1); }}>Sort: Name (Z - A)</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
      </div>

      {isSearching && (
        <div className="d-flex justify-content-center align-items-center my-3">
          <ProgressDialog show={isSearching} message="Searching..." inline />
        </div>
      )}

      {error && <p className="text-danger">{error}</p>}

      {/* Main Grid Card View */}
      <div className="suppliers-grid">
        {paginatedSuppliers.length === 0 && !loading && !isSearching ? (
          <div className="grid-no-data py-5 text-center w-100">
            <NoDataCard
              title="No Suppliers Found"
              message="Adjust query to find vendors."
              icon="🏢"
              actionButton={
                <Button variant="primary" onClick={() => handleShowModal()} className="card-hover">
                  <FaPlus /> Add New Supplier
                </Button>
              }
            />
          </div>
        ) : (
          paginatedSuppliers.map((supplier) => (
            <div className="supplier-card card-hover" key={supplier.supplier_id}>
              <div className="supplier-card-top mb-3">
                <div className="supplier-avatar-wrapper">
                  <div className="supplier-avatar-text">{supplier.name?.charAt(0) || 'S'}</div>
                </div>
                <div className="supplier-info-header">
                  <h3 className="supplier-title-text" title={supplier.name}>{supplier.name}</h3>
                  {supplier.contact_person && (
                    <span className="contact-person-badge">
                      <FaUser className="me-1" style={{ fontSize: '0.7rem' }} /> {supplier.contact_person}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="supplier-body mb-3">
                {supplier.phone && (
                  <div className="supplier-contact-line">
                    <FaPhone className="text-muted flex-shrink-0" />
                    <span className="contact-value-text">{supplier.phone}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="supplier-contact-line">
                    <FaEnvelope className="text-muted flex-shrink-0" />
                    <span className="contact-value-text" title={supplier.email}>{supplier.email}</span>
                  </div>
                )}
                {supplier.address && (
                  <div className="supplier-contact-line align-items-start">
                    <FaMapMarkerAlt className="text-muted flex-shrink-0 mt-1" />
                    <span className="contact-value-text address-block">{supplier.address}</span>
                  </div>
                )}
                <div className="supplier-gst-tag-wrapper mt-2">
                  <span className={`gst-badge ${supplier.gst_number ? 'gst-yes' : 'gst-no'}`}>
                    <FaFileInvoice className="me-1" /> GST: {supplier.gst_number || '—'}
                  </span>
                </div>
              </div>

              <div className="supplier-card-actions mt-auto">
                <Button size="sm" onClick={() => handleShowModal(supplier)} className="edit-btn-supplier card-hover">
                  <FaEdit /> Edit
                </Button>
                <Button size="sm" onClick={() => handleDelete(supplier.supplier_id)} className="delete-btn-supplier card-hover">
                  <FaTrash /> Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {sortedSuppliers.length > 0 && totalPages > 1 && (
        <Pagination className="custom-pagination justify-content-center mt-4">
          <Pagination.Prev onClick={() => handlePageChange(Math.max(1, currentPage - 1))} />
          {[...Array(totalPages).keys()].map((number) => (
            <Pagination.Item
              key={number + 1}
              active={number + 1 === currentPage}
              onClick={() => handlePageChange(number + 1)}
            >
              {number + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} />
        </Pagination>
      )}

      {/* Save Modal */}
      <Modal show={showModal} onHide={handleCloseModal} scrollable className="supplier-modal-modern" size="lg">
        <Modal.Header closeButton className="sup-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="sup-modal-icon">
              <FaBuilding />
            </div>
            <div>
              <Modal.Title className="sup-modal-title mb-0">
                {currentSupplier ? 'Edit Supplier Configuration' : 'Register New Supplier'}
              </Modal.Title>
              <div className="sup-modal-subtitle">
                {currentSupplier ? `Configuring Profile ID #${currentSupplier.supplier_id} (${currentSupplier.name})` : 'Register a new distributor or manufacturing merchant profile'}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Form onSubmit={handleSubmit} className="sup-modal-form-wrapper">
          <Modal.Body className="sup-modal-body p-4">
            {error && <Container className="mb-3" style={{ color: 'red' }}>Error: {error}</Container>}
            
            <Row className="g-4">
              {/* Left Column: Commercial profile */}
              <Col lg={6}>
                <div className="sup-form-section-title mb-3">
                  <span className="section-badge">1</span> Company Profile
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label className="sup-field-label">Supplier/Merchant Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    name="name" 
                    value={form.name} 
                    onChange={handleChange} 
                    placeholder="e.g. Acme Pharmaceuticals Ltd" 
                    required 
                    className="sup-modal-input"
                  />
                  <div className="sup-modal-hint mt-1">Official registered commercial entity name.</div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="sup-field-label">GSTIN / Tax ID Number</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="gst_number" 
                    value={form.gst_number} 
                    onChange={handleChange} 
                    placeholder="e.g. 27AAAAA1111A1Z1" 
                    className="sup-modal-input"
                  />
                  <div className="sup-modal-hint mt-1">Provide supplier's 15-digit GST registration code.</div>
                </Form.Group>
              </Col>

              {/* Right Column: Contact info */}
              <Col lg={6}>
                <div className="sup-form-section-title mb-3">
                  <span className="section-badge">2</span> Communication & Address
                </div>

                <Form.Group className="mb-3">
                  <Form.Label className="sup-field-label">Primary Contact Representative</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="contact_person" 
                    value={form.contact_person} 
                    onChange={handleChange} 
                    placeholder="e.g. John Doe (Sales Exec)" 
                    className="sup-modal-input"
                  />
                </Form.Group>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="sup-field-label">Phone Number</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="phone" 
                        value={form.phone} 
                        onChange={handleChange} 
                        placeholder="e.g. +91 98765 43210" 
                        className="sup-modal-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="sup-field-label">Email Address</Form.Label>
                      <Form.Control 
                        type="email" 
                        name="email" 
                        value={form.email} 
                        onChange={handleChange} 
                        placeholder="e.g. billing@acme.com" 
                        className="sup-modal-input"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group>
                  <Form.Label className="sup-field-label">Mailing Address</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={3} 
                    name="address" 
                    value={form.address} 
                    onChange={handleChange} 
                    placeholder="Enter full physical dispatch address..." 
                    className="sup-modal-textarea"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer className="sup-modal-footer d-flex justify-content-end py-3 px-4">
            <Button variant="primary" type="submit" className="sup-submit-btn fw-bold px-4" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : (currentSupplier ? 'Update Supplier Profile' : 'Register Supplier')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} dialogClassName="theme-dialog" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this supplier?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SupplierManagement;